import { Clock, Minus, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';

import logoColor from '@/assets/brand/signcare-logo.svg';
import shieldTick from '@/assets/icons/shield-tick.svg';
import { cn } from '@/lib/utils';

import { StepIcon } from '../../../stepIcons';

// Welcome / overview screen — the FIRST screen of every journey (designer:
// react-new-html `VerificationStepsScreen`, `.sc-verification-steps__*`).
//
// It lists the workflow's steps in order with the number of inputs the applicant
// has to fill for each, so they know what's coming before they start. Everything
// around the list is fixed copy: the logo, the two heading lines, the DPDP notice
// and the consent paragraph.
//
// The list is grouped by Console's API CATEGORY: one collapsed row per category
// ("KYC & Identity Verification"), tapping it reveals the workflow's steps inside.
// That keeps the first screen short — three rows instead of ten — while still
// letting the applicant see exactly what is coming before they start.
//
// Categories come from `api_catalogue.category`, which mirrors Console's
// catalog_categories. Nothing here invents a grouping.

/** One row of the overview list — one workflow step. */
export interface WelcomePreviewStep {
  /** Stable key (the workflow step id). */
  id: string;
  /** Step type, drives the designer icon. */
  stepType: string;
  /** Display name, e.g. "PAN Verification". */
  name: string;
  /**
   * Console's top-level category for this step's API, e.g.
   * "KYC & Identity Verification". Blank when the step has no catalogue entry —
   * those collect under {@link UNCATEGORISED} rather than disappearing.
   */
  category?: string;
}

/** Heading for steps whose API carries no category. Never hide a step. */
const UNCATEGORISED = 'Other Verification';

/**
 * Group heading for Questionnaire steps.
 *
 * They have no catalogue entry — they call no provider — so they fell into
 * {@link UNCATEGORISED} and were listed to the applicant under "Other VERIFICATION". Nothing is being
 * verified: they are being asked questions, and a heading that says otherwise misdescribes what the
 * screen will do and what the consent notice below it covers.
 */
export const QUESTIONNAIRE_CATEGORY = 'Additional Information';

/**
 * Group heading for the Email Verification step.
 *
 * It has no catalogue entry either — we send the code ourselves — so it would fall into
 * {@link UNCATEGORISED} and be listed under "Other Verification". Unlike Questionnaire, something IS
 * being verified here, so it gets a heading that names it rather than being folded into
 * {@link QUESTIONNAIRE_CATEGORY}. Its own group also means the applicant can see it coming: "we will
 * email you" is the one step in a run that requires them to leave the page.
 */
export const EMAIL_VERIFICATION_CATEGORY = 'Contact Verification';

/** Default heading, used until the workflow overrides it in Settings. */
export const WELCOME_HEADING_DEFAULT = 'Onboarding Verification';
/** Default sub-heading, used until the workflow overrides it in Settings. */
export const WELCOME_SUBHEADING_DEFAULT = 'Complete these steps to finish onboarding';

/**
 * Length caps for the Settings overrides, enforced where they're typed.
 *
 * They live here rather than in the Settings drawer because this screen is what
 * they protect: the heading renders at 22px and the sub-heading at 14px inside a
 * ~300px-wide phone, so past these lengths the text wraps far enough to push the
 * step list and the notice out of view. Sized off the defaults above (23 and 41
 * characters) with room to spare — roughly two lines each.
 */
export const WELCOME_HEADING_MAX_LENGTH = 40;
export const WELCOME_SUBHEADING_MAX_LENGTH = 80;

/** Rough minutes-per-step used for the "Approx. N min" badge (designer's figure). */
const MINUTES_PER_STEP = 2;

export function WelcomeScreen({
  steps,
  heading = WELCOME_HEADING_DEFAULT,
  subheading = WELCOME_SUBHEADING_DEFAULT,
  logoUrl,
  hideLogo = false,
  className,
}: {
  steps: WelcomePreviewStep[];
  heading?: string;
  subheading?: string;
  /** Tenant logo from Customization; falls back to the SignCare mark. */
  logoUrl?: string | null;
  /** Skip the logo — for hosts (the live run) that already render one above. */
  hideLogo?: boolean;
  /**
   * Extra root classes. The live run passes `min-h-0 flex-1` so the screen GROWS
   * to fill its card instead of relying on `h-full` resolving through a chain of
   * flex parents — which it doesn't do reliably, and when it fails the DPDP
   * notice floats mid-card with a gap under it.
   */
  className?: string;
}) {
  // Still per STEP, not per category — the estimate is about work to do, and
  // grouping the display doesn't make the run shorter.
  const minutes = steps.length * MINUTES_PER_STEP;

  // Grouped in step order, so the category holding step 1 is listed first.
  const groups = useMemo(() => {
    const byCategory = new Map<string, WelcomePreviewStep[]>();
    for (const step of steps) {
      const name = step.category?.trim() || UNCATEGORISED;
      const list = byCategory.get(name);
      if (list) {
        list.push(step);
      } else {
        byCategory.set(name, [step]);
      }
    }
    return [...byCategory.entries()].map(([name, groupSteps]) => ({ name, steps: groupSteps }));
  }, [steps]);

  // ALWAYS collapsed on arrival, however many categories there are — the designer's
  // first screen is a short list of closed cards with a "+" on each. No auto-open
  // special case: one category behaving differently from two is the kind of
  // inconsistency that reads as a bug.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  function toggle(name: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(name)) {
        next.add(name);
      }
      return next;
    });
  }

  return (
    // h-full so the screen fills the phone's scroll area in the builder preview:
    // the header block and the DPDP notice stay put, and the steps list in between
    // takes whatever height is left and scrolls on its own (designer's fixed-height
    // list). Hosts whose card is sized by flex pass `flex-1` via className instead.
    <div className={cn('flex h-full w-full flex-col items-center text-center', className)}>
      {/* Brand logo — the tenant's from Customization, else the SignCare mark. */}
      {!hideLogo && (
        <div className="mb-3 mt-1 flex w-full shrink-0 items-center justify-center">
          <img
            src={logoUrl || logoColor}
            alt={logoUrl ? 'Organization logo' : 'SignCare'}
            className="max-h-10 max-w-[156px] object-contain"
          />
        </div>
      )}

      {/* Heading + sub-heading */}
      <div className="mb-2.5 flex shrink-0 flex-col items-center text-center">
        <h2
          className="m-0 mb-1 text-[22px] font-semibold leading-tight"
          style={{ color: 'var(--brand-heading, #041A3A)' }}
        >
          {heading}
        </h2>
        <p
          className="m-0 text-[length:var(--text-14)] leading-tight"
          style={{ color: 'var(--brand-body, #666666)' }}
        >
          {subheading}
        </p>
      </div>

      {/* Time estimate */}
      <div className="mb-5 inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[#99B6FF] px-2 py-1 text-[length:var(--text-12)] font-medium text-[#336DFF]">
        <Clock size={13} />
        <span>Approx. {minutes} min</span>
      </div>

      {/* Steps list — one card per CATEGORY, expanding to the steps inside. Takes
          the space left between the fixed header and the fixed notice below, and
          scrolls inside itself once there is more than fits (designer behaviour:
          only this list moves, the rest of the screen stays put). */}
      <div className="custom-scrollbar mb-4 flex min-h-0 w-full flex-1 flex-col gap-2 overflow-y-auto pr-1 text-left">
        {steps.length === 0 ? (
          <p className="py-6 text-center text-[length:var(--text-12)] text-placeholder">
            No verification steps selected
          </p>
        ) : (
          groups.map((group) => {
            const open = expanded.has(group.name);
            return (
              <div
                key={group.name}
                className="shrink-0 overflow-hidden rounded-lg border border-[#B3B3B3] bg-white"
              >
                <button
                  type="button"
                  onClick={() => toggle(group.name)}
                  aria-expanded={open}
                  className="flex h-12 w-full items-center justify-between border-none bg-transparent px-3.5 text-start"
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    {/* The first step's icon stands in for the category — it is the
                        one the applicant meets first, and inventing a per-category
                        icon set would be a second taxonomy to keep in sync. */}
                    <StepIcon
                      stepType={group.steps[0]?.stepType ?? ''}
                      className="shrink-0 text-[#374151]"
                    />
                    {/* Category name follows the branded heading colour, the counts
                        the branded body colour — they're this screen's own text, so
                        a tenant palette has to reach them too. Fallbacks are the
                        designer's fixed colours, so the default look is unchanged. */}
                    <span
                      className="truncate text-[length:var(--text-14)] font-medium"
                      style={{ color: 'var(--brand-heading, #041A3A)' }}
                    >
                      {group.name}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {/* How many APIs of this category the run uses — so the closed
                        card still says how much work is inside it. Counts STEPS,
                        not input fields: the number matches the numbered list that
                        appears when it opens. */}
                    <span
                      className="text-[length:var(--text-14)]"
                      style={{ color: 'var(--brand-body, #666666)' }}
                    >
                      {group.steps.length} {group.steps.length === 1 ? 'step' : 'steps'}
                    </span>
                    {open ? (
                      <Minus
                        size={14}
                        style={{ color: 'var(--brand-body, #666666)' }}
                        aria-hidden="true"
                      />
                    ) : (
                      <Plus
                        size={14}
                        style={{ color: 'var(--brand-body, #666666)' }}
                        aria-hidden="true"
                      />
                    )}
                  </div>
                </button>

                {/* The APIs of this category that this workflow actually uses —
                    never the whole catalogue. Number + name, nothing else.
                    The number is the step's real place in the WHOLE run, not its
                    place in this group — so a second category continues 5, 6, …
                    and the numbering matches the order the applicant meets them. */}
                {open && (
                  <ol className="m-0 list-none border-t border-[#EFEFEF] p-0">
                    {group.steps.map((step) => (
                      <li key={step.id} className="flex items-center gap-2.5 px-3.5 py-2.5">
                        <span className="flex w-4 shrink-0 items-center justify-center">
                          {/* The step's own icon, not its position number. Same
                              `StepIcon` the editor sidebar uses, so a verification is
                              recognisable by the same mark everywhere it appears. */}
                          <StepIcon stepType={step.stepType} className="!h-4 !w-4" />
                        </span>
                        <span
                          className="truncate text-[length:var(--text-12)]"
                          style={{ color: 'var(--brand-heading, #041A3A)' }}
                        >
                          {step.name}
                        </span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* DPDP data-protection notice — fixed copy, pinned above the CTA. */}
      <div className="flex shrink-0 items-start gap-2 rounded-lg bg-[#F4F4F4] p-3 text-left">
        <img src={shieldTick} alt="" width={16} height={16} className="mt-0.5 shrink-0" />
        <p className="m-0 text-[length:var(--text-12)] leading-snug text-gray66">
          Processes under the digital personal data protection Act, 2023. Only data required for
          these checks is collected, and it is not retained beyond the required period.
        </p>
      </div>
    </div>
  );
}

/**
 * Consent paragraph shown under the welcome screen's Continue button.
 *
 * Lives in the phone's fixed footer (not the scroll area) so it sits directly
 * beneath the CTA, matching the design's order: Continue → consent → Secured by.
 */
export function WelcomeConsentNotice() {
  return (
    <p className="m-0 px-1 text-center text-[length:var(--text-10)] leading-snug text-gray66">
      By selecting "Continue" you consent to processing of your PAN, Aadhaar, financial, and
      biometric data as per the{' '}
      <span className="font-medium text-primary underline underline-offset-2">Consent Notice</span>{' '}
      and{' '}
      <span className="font-medium text-primary underline underline-offset-2">Privacy Policy</span>,
      under the DPDP Act, 2023. Consent may be withdrawn anytime from account settings.
    </p>
  );
}
